import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { ApiService } from '../../../services/api.service';
import { isPlatformBrowser } from '@angular/common';
import { MediaCacheService } from '../../../services/media-cache.service';
import { CommonService } from '../../../services/common.service';
import { ApiCacheService } from '../../../services/api-cache.service';
import { OrDialogService } from '../../../ui';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { LucideIconsModule } from '../../../shared/lucide-icons/lucide-icons.module';
import {
  resolveQueryTab,
} from '../../../utils/resolve-query-tab.util';
import {
  SETTINGS_TAB_ALIASES,
  SETTINGS_TABS,
} from '../../../pages/settings/settings-tabs.constants';
import {
  resolveSettingsPath,
  SETTINGS_PAGE_PATHS,
} from '../../../pages/settings/settings-routes.constants';
import {
  ALL_SIDE_MENU_SECTIONS,
  SideMenuItem,
  cloneSideMenuSections,
  filterSideMenuSections,
  flattenSideMenuSections,
} from '../../../utils/side-menu.config';
import { allowedPathsForRole } from '../../../utils/rbac-routes.util';
import {
  filterOutletsByUserScope,
  pickScopedOutlet,
} from '../../../utils/outlet-scope.util';
import { filter } from 'rxjs/operators';
import { Subscription, combineLatest, startWith } from 'rxjs';
import { ThemeMode, ThemeService } from '../../../services/theme.service';
import { BillingApiService } from '../../../services/billing-api.service';
import { UserAccessService } from '../../../services/user-access.service';

@Component({
  selector: 'app-header',
  imports: [
    NzIconModule,
    NzGridModule,
    NzInputModule,
    NzDropDownModule,
    NzSpinModule,
    NzModalModule,
    RouterLink,
    FormsModule,
    LucideIconsModule,
  ],

  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('searchResultsContainer') searchResultsContainer?: ElementRef<HTMLElement>;
  isDisabled = true;
  isVisible = false;
  isMac: boolean = false;

  userName: any = {};
  selectOutlet: any = {};
  allOutlet: any[] = [];
  /** Unfiltered API results — re-filtered when outletScope loads. */
  private rawOutlets: any[] = [];
  private userOutletScope: string[] = [];

  outletSearch = '';
  outletSearchExpanded = false;
  readonly outletLimit = 50;
  outletPageIndex = 1;
  outletTotalCount = 0;
  outletsLoading = false;
  outletLoadingMore = false;

  searchTimeOut: any;
  outletSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  searchText: string = '';
  searchType: 'pages' | 'members' = 'pages';
  filteredMembers: any[] = [];
  membersLoading = false;
  activeSearchIndex = -1;
  isKeyboardSearchNav = false;
  private membersSearchTimeout: any;

  get isMobileView(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return window.innerWidth <= 768;
    }
    return false;
  }

  get modalWidth(): number | string {
    return this.isMobileView ? '100%' : 560;
  }

  private api = inject(ApiService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private readonly dialog = inject(OrDialogService);
  private commonServices = inject(CommonService);
  platformId = inject(PLATFORM_ID);
  private mediaCache = inject(MediaCacheService);
  private apiCache = inject(ApiCacheService);
  private theme = inject(ThemeService);
  private billingApi = inject(BillingApiService);
  private userAccess = inject(UserAccessService);
  private cdr = inject(ChangeDetectorRef);
  themeMode: ThemeMode = 'light';

  private readonly allSideMenuSections = ALL_SIDE_MENU_SECTIONS;
  sideMenuSections = cloneSideMenuSections(this.allSideMenuSections);
  private sideMenuFlat: SideMenuItem[] = flattenSideMenuSections(this.sideMenuSections);
  private settingsSubscriptions = new Subscription();
  private allowedMenuPaths: readonly string[] | null = null;

  planLabel = '';
  planDaysLeft: number | null = null;
  planProgress = 0;
  planCta = 'Upgrade';
  planLoaded = false;
  planIsTrial = false;

  /** Cached search results — rebuilt when searchText / menu changes. */
  filteredSideMenu: SideMenuItem[] = this.sideMenuFlat;
  otherFilteredOutlets: any[] = [];
  userDisplayName = 'Super Admin';
  userInitials = 'SA';

  get sideMenu(): SideMenuItem[] {
    return this.sideMenuFlat;
  }

  ngOnInit(): void {
    this.themeMode = this.theme.current();
    this.isMac = this.checkPlatform();
    this.getLoginData();
    const storedOutlet = localStorage.getItem('selectedOutlet');
    if (storedOutlet) {
      const data = JSON.parse(storedOutlet);
      this.selectOutlet = data.outlet || {};
      this.refreshSideMenu(data.outlet?.posName);
    }
    this.getAllOutlets();

    this.settingsSubscriptions.add(
      combineLatest([
        this.commonServices.selectedOutlet.pipe(startWith(this.selectOutlet || null)),
        this.userAccess.fetchAccess(),
      ]).subscribe(([outlet, access]) => {
        this.allowedMenuPaths = allowedPathsForRole(access.role);
        const scope = access.outletScope || [];
        if (scope.join(',') !== this.userOutletScope.join(',')) {
          this.userOutletScope = scope;
          this.applyOutletScopeFilter(true);
        }
        this.refreshSideMenu((outlet as any)?.posName, access.role);
        this.cdr.markForCheck();
      }),
    );

    this.redirectLegacySettingsTabQuery();
    this.settingsSubscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => this.redirectLegacySettingsTabQuery()),
    );
    this.loadPlanSummary();
  }

  private refreshSideMenu(posName?: string | null, role?: string | null): void {
    this.sideMenuSections = filterSideMenuSections(
      this.allSideMenuSections,
      posName,
      this.allowedMenuPaths,
      role,
    );
    this.sideMenuFlat = flattenSideMenuSections(this.sideMenuSections);
    this.rebuildFilteredSideMenu();
    this.cdr.markForCheck();
  }

  private loadPlanSummary(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.settingsSubscriptions.add(
      this.billingApi.getCurrent().subscribe({
        next: (response: any) => {
          const sub = response?.data?.subscription;
          if (!sub) {
            this.planLoaded = false;
            return;
          }
          const planName = String(sub.planCode || 'starter');
          const label = planName.charAt(0).toUpperCase() + planName.slice(1);
          this.planIsTrial = sub.status === 'trialing';
          this.planLabel = this.planIsTrial ? `${label} · Trial` : `${label} plan`;
          this.planCta = this.planIsTrial || planName === 'starter' ? 'Upgrade' : 'Manage';

          if (this.planIsTrial && sub.trialEndsAt) {
            const totalMs = 14 * 86400000;
            const leftMs = new Date(sub.trialEndsAt).getTime() - Date.now();
            this.planDaysLeft = Math.max(Math.ceil(leftMs / 86400000), 0);
            this.planProgress = Math.min(Math.max(((totalMs - leftMs) / totalMs) * 100, 0), 100);
          } else {
            this.planDaysLeft = null;
            this.planProgress = 100;
          }
          this.planLoaded = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.planLoaded = false;
          this.cdr.markForCheck();
        },
      }),
    );
  }

  toggleTheme(): void {
    this.themeMode = this.theme.toggle();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.settingsSubscriptions.unsubscribe();
    if (this.membersSearchTimeout) {
      clearTimeout(this.membersSearchTimeout);
    }
  }

  private redirectLegacySettingsTabQuery(): void {
    const tab = this.leafRoute.snapshot.queryParamMap.get('tab');
    if (!tab) return;
    const resolved = resolveQueryTab(
      tab,
      SETTINGS_TABS,
      SETTINGS_TAB_ALIASES,
    );
    if (!resolved) {
      return;
    }
    // Only migrate legacy settings ?tab= deep links — never hijack other pages'
    // own ?tab= values (products, feedback, members, …).
    const path = this.router.url.split('?')[0].split(';')[0];
    const looksLikeLegacySettings =
      path === '/' ||
      path === '/dashboard' ||
      path.startsWith('/settings') ||
      path.startsWith('/mobile-setting');
    if (!looksLikeLegacySettings) {
      return;
    }
    this.router.navigateByUrl(resolveSettingsPath(resolved), { replaceUrl: true });
  }

  private get leafRoute(): ActivatedRoute {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
  get filteredOutlets(): any[] {
    return this.allOutlet;
  }

  get currentOutlet(): any | null {
    const selectedId = this.selectOutlet?._id;
    if (!selectedId) {
      return null;
    }
    return (
      this.allOutlet.find((item) => item._id === selectedId) ?? this.selectOutlet
    );
  }

  get hasMoreOutlets(): boolean {
    if (this.outletTotalCount <= 0) {
      return false;
    }
    return this.allOutlet.length < this.outletTotalCount;
  }

  private refreshUserDisplay(): void {
    const firstName = (this.userName?.firstName || '').trim();
    const lastName = (this.userName?.lastName || '').trim();
    const fullName = `${firstName} ${lastName}`.trim();
    this.userDisplayName = fullName || 'Super Admin';
    const parts = this.userDisplayName.split(/\s+/).filter(Boolean);
    this.userInitials =
      parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'OR';
  }

  private rebuildOtherOutlets(): void {
    const selectedId = this.selectOutlet?._id;
    this.otherFilteredOutlets = !selectedId
      ? this.allOutlet
      : this.allOutlet.filter((item) => item._id !== selectedId);
  }

  private rebuildFilteredSideMenu(): void {
    const query = this.searchText.trim().toLowerCase();
    this.filteredSideMenu = !query
      ? this.sideMenuFlat
      : this.sideMenuFlat.filter((item) =>
          item.name.toLowerCase().includes(query),
        );
  }

  @ViewChild('outletSearchInput') outletSearchInput?: ElementRef<HTMLInputElement>;

  onOutletDropdownVisible(visible: boolean): void {
    if (visible) {
      this.resetAndFetchOutlets();
      return;
    }
    this.outletSearchExpanded = false;
    this.outletSearch = '';
  }

  openOutletSearch(event: Event): void {
    event.stopPropagation();
    this.outletSearchExpanded = true;
    setTimeout(() => this.outletSearchInput?.nativeElement?.focus(), 0);
  }

  closeOutletSearch(event: Event): void {
    event.stopPropagation();
    this.outletSearchExpanded = false;
    this.clearOutletSearch();
  }

  clearOutletSearch(event?: Event): void {
    event?.stopPropagation();
    if (this.outletSearchTimeout) {
      clearTimeout(this.outletSearchTimeout);
    }
    this.outletSearch = '';
    this.resetAndFetchOutlets();
    if (this.outletSearchExpanded) {
      setTimeout(() => this.outletSearchInput?.nativeElement?.focus(), 0);
    }
  }

  onOutletSearchChange(): void {
    if (this.outletSearchTimeout) {
      clearTimeout(this.outletSearchTimeout);
    }
    this.outletSearchTimeout = setTimeout(() => {
      this.resetAndFetchOutlets();
    }, 300);
  }

  resetAndFetchOutlets(): void {
    this.outletPageIndex = 1;
    this.allOutlet = [];
    this.outletLoadingMore = false;
    this.fetchOutlets();
  }

  onOutletListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el || this.outletsLoading || this.outletLoadingMore || !this.hasMoreOutlets) {
      return;
    }
    const threshold = 24;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadMoreOutlets();
    }
  }

  loadMoreOutlets(): void {
    if (!this.hasMoreOutlets || this.outletLoadingMore || this.outletsLoading) {
      return;
    }
    this.outletLoadingMore = true;
    this.outletPageIndex += 1;
    this.fetchOutlets();
  }

  getAllOutlets() {
    this.outletPageIndex = 1;
    this.allOutlet = [];
    this.rawOutlets = [];
    this.fetchOutlets(true);
  }

  /** Restrict outlet picker to the user's assigned scope (billers, etc.). */
  private applyOutletScopeFilter(reselect = false): void {
    this.allOutlet = filterOutletsByUserScope(this.rawOutlets, this.userOutletScope);
    this.commonServices.registerOutlets(this.allOutlet);
    this.rebuildOtherOutlets();

    if (!reselect) {
      this.cdr.markForCheck();
      return;
    }

    const picked = pickScopedOutlet(this.allOutlet, this.selectOutlet);
    if (picked) {
      this.selectOutlet = picked;
      this.applyOutlet(picked);
    } else if (!this.allOutlet.length) {
      this.selectOutlet = {};
      this.commonServices.updateSelectedOutlet({});
    }
    this.rebuildOtherOutlets();
    this.cdr.markForCheck();
  }

  private fetchOutlets(isInitialLoad = false) {
    const tokenData: string =
      localStorage.getItem('TtZUw0Q87E647ac8j0gQHiCmBhczph') || '';
    if (!tokenData) return;

    const requestedPage = this.outletPageIndex;
    if (requestedPage === 1) {
      this.outletsLoading = true;
    }

    const header = {
      headers: {
        Authorization: 'Bearer ' + tokenData,
      },
    };

    const payload: Record<string, string> = {};
    if (this.outletSearch.trim()) {
      payload['filterValue'] = this.outletSearch.trim();
    }

    this.api
      .commonPostMethod(
        `outlet?page=${requestedPage}&limit=${this.outletLimit}`,
        payload,
        header,
      )
      .subscribe(
        (response: any) => {
          const data = response?.data?.outlet || [];

          if (requestedPage === 1) {
            this.rawOutlets = data;
          } else {
            this.rawOutlets = [...this.rawOutlets, ...data];
          }

          this.applyOutletScopeFilter(isInitialLoad);

          this.outletTotalCount =
            response?.data?.pagination?.totalCount ?? this.allOutlet.length;

          this.outletsLoading = false;
          this.outletLoadingMore = false;
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.outletsLoading = false;
          this.outletLoadingMore = false;
          this.cdr.markForCheck();
          if (requestedPage > 1) {
            this.outletPageIndex -= 1;
          }
          console.log(error);
          if (
            error?.error?.message == 'jwt expired' ||
            error?.error?.message == 'jwt must be provided' ||
            error?.error?.message == 'jwt malformed' ||
            error?.error?.message == 'invalid signature' ||
            error?.error?.message == 'Given token user does not exist.' ||
            error?.error?.message == 'invalid token'
          ) {
            this.commonServices.invalidToken();
          }
        },
      );
  }

  applyOutlet(item: any) {
    const previousOutletId = this.selectOutlet
      ? typeof this.selectOutlet === 'object'
        ? this.selectOutlet._id
        : this.selectOutlet
      : '';

    // Defensive normalization: callers may pass either the full outlet
    // object or just an id string. We always want to store and broadcast
    // the full outlet detail so downstream consumers (localStorage,
    // side-menu filter, currency sync, etc.) get the complete shape.
    const resolvedOutlet =
      item && typeof item === 'object'
        ? item
        : this.allOutlet.find((o) => o?._id === item) ?? { _id: item };

    this.selectOutlet = resolvedOutlet;
    this.commonServices.updateSelectedOutlet(resolvedOutlet);
    this.selectOutlet = this.commonServices.selectedOutlet.value;
    this.rebuildOtherOutlets();
    this.cdr.markForCheck();

    const newOutletId =
      (this.selectOutlet as any)?._id || resolvedOutlet?._id || '';
    if (previousOutletId && previousOutletId !== newOutletId) {
      // Wipe all — unscoped entries (empty outletId) would survive invalidateOutlet.
      this.apiCache.invalidateAll();
    }
  }

  selectedOutletLabel: string = '';
  selectOutletFn(item: any) {
    this.selectOutlet = item;
    this.selectedOutletLabel = item?.outletName || '';
    this.applyOutlet(item);
  }
  getLoginData() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.settingsSubscriptions.add(
      this.userAccess.fetchUserProfile().subscribe((profile) => {
        if (profile) {
          this.userName = profile;
          this.refreshUserDisplay();
          this.cdr.markForCheck();
        }
      }),
    );
  }

  async logOut(): Promise<void> {
    const ok = await this.dialog.confirm({
      title: 'Log out?',
      message: 'You will need to sign in again to continue.',
      confirmLabel: 'Log out',
    });
    if (!ok) return;
    this.commonServices.clearLocalStorage();
    this.mediaCache.clearCache();
    this.router.navigate(['/login']);
  }
  get navigableSearchResults(): Array<SideMenuItem | any> {
    if (this.searchType === 'pages') {
      return this.filteredSideMenu;
    }
    const query = this.searchText.trim();
    if (!query || this.membersLoading) {
      return [];
    }
    return this.filteredMembers;
  }

  isSearchResultActive(index: number): boolean {
    return this.activeSearchIndex === index;
  }

  showModal(): void {
    this.isVisible = true;
    this.searchText = '';
    this.searchType = 'pages';
    this.filteredMembers = [];
    this.activeSearchIndex = -1;
    this.isKeyboardSearchNav = false;
    setTimeout(() => {
      this.searchInput.nativeElement.focus();
    }, 500);
  }

  handleCancel(): void {
    this.isVisible = false;
  }
  modalclose() {
    this.isVisible = false;
  }

  hasNoResults(): boolean {
    const query = this.searchText.trim();
    if (query.length === 0) {
      return false;
    }
    if (this.searchType === 'pages') {
      return this.filteredSideMenu.length === 0;
    } else {
      return this.filteredMembers.length === 0 && !this.membersLoading;
    }
  }

  changeSearchType(type: 'pages' | 'members'): void {
    this.searchType = type;
    this.searchText = '';
    this.filteredMembers = [];
    this.activeSearchIndex = -1;
    this.isKeyboardSearchNav = false;
    if (this.membersSearchTimeout) {
      clearTimeout(this.membersSearchTimeout);
    }
    setTimeout(() => {
      this.searchInput.nativeElement.focus();
    }, 0);
  }

  onSearchTextChange(): void {
    this.activeSearchIndex = -1;
    this.isKeyboardSearchNav = false;
    if (this.searchType !== 'members') {
      this.rebuildFilteredSideMenu();
      this.cdr.markForCheck();
      return;
    }
    if (this.membersSearchTimeout) {
      clearTimeout(this.membersSearchTimeout);
    }

    const query = this.searchText.trim();
    if (!query) {
      this.filteredMembers = [];
      this.cdr.markForCheck();
      return;
    }

    this.membersSearchTimeout = setTimeout(() => {
      this.searchMembers(query);
    }, 300);
  }

  searchMembers(query: string): void {
    const tokenData: string = localStorage.getItem('TtZUw0Q87E647ac8j0gQHiCmBhczph') || '';
    if (!tokenData) return;

    this.membersLoading = true;
    const outletId = (typeof this.selectOutlet === 'object') ? this.selectOutlet?._id : this.selectOutlet;

    const payload = {
      outletId: outletId || undefined,
      filterValue: query
    };

    const header = {
      headers: {
        Authorization: 'Bearer ' + tokenData,
      },
    };

    this.api.commonPostMethod('loyalty-customer/get-All-customerPoints?page=1&limit=50', payload, header)
      .subscribe({
        next: (response: any) => {
          if (this.searchText.trim() === query) {
            this.filteredMembers = response?.data?.loyaltyCustomer || [];
          }
          this.membersLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error(error);
          this.membersLoading = false;
          this.filteredMembers = [];
          this.cdr.markForCheck();
        }
      });
  }

  goToMemberDetail(member: any): void {
    this.modalclose();
    this.router.navigate(['/customer-analytics'], {
      queryParams: {
        customerId: member._id,
        customerName: member.name || 'Customer',
        phoneNo: member.phoneNo || '',
        fromTab: 'members',
      },
    });
  }

  onSearchInputKeydown(event: KeyboardEvent): void {
    if (!this.isVisible) {
      return;
    }

    const items = this.navigableSearchResults;
    if (event.key === 'ArrowDown') {
      if (items.length === 0) {
        return;
      }
      event.preventDefault();
      this.isKeyboardSearchNav = true;
      this.activeSearchIndex =
        this.activeSearchIndex < items.length - 1
          ? this.activeSearchIndex + 1
          : 0;
      this.scrollActiveSearchResultIntoView();
      return;
    }

    if (event.key === 'ArrowUp') {
      if (items.length === 0) {
        return;
      }
      event.preventDefault();
      this.isKeyboardSearchNav = true;
      this.activeSearchIndex =
        this.activeSearchIndex > 0
          ? this.activeSearchIndex - 1
          : items.length - 1;
      this.scrollActiveSearchResultIntoView();
      return;
    }

    if (event.key === 'Enter' && this.activeSearchIndex >= 0) {
      const selected = items[this.activeSearchIndex];
      if (!selected) {
        return;
      }
      event.preventDefault();
      this.activateSearchResult(selected);
    }
  }

  onSearchResultsPointerInteraction(): void {
    if (!this.isKeyboardSearchNav) {
      return;
    }
    this.isKeyboardSearchNav = false;
    this.activeSearchIndex = -1;
  }

  private activateSearchResult(item: SideMenuItem | any): void {
    if (this.searchType === 'pages') {
      this.modalclose();
      this.router.navigate([(item as SideMenuItem).path]);
      return;
    }
    this.goToMemberDetail(item);
  }

  private scrollActiveSearchResultIntoView(): void {
    setTimeout(() => {
      const container = this.searchResultsContainer?.nativeElement;
      if (!container) {
        return;
      }
      const activeItem = container.querySelector(
        '.search-result-item.active',
      ) as HTMLElement | null;
      activeItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  private checkPlatform(): boolean {
    return /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (
      (this.isMac && event.metaKey && event.key.toLowerCase() === 'k') ||
      (!this.isMac && event.ctrlKey && event.key.toLowerCase() === 'k')
    ) {
      event.preventDefault();
      this.showModal();
    }
  }
  stopFocus(event: MouseEvent) {
    event.preventDefault();
  }

  open(menu: string = 'setting-business-details'): void {
    this.router.navigateByUrl(resolveSettingsPath(menu));
  }

  openProfile(): void {
    this.router.navigateByUrl(SETTINGS_PAGE_PATHS.profile);
  }

  goToCreateOutlet(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate([SETTINGS_PAGE_PATHS.outlets], {
      queryParams: { create: '1' },
    });
  }

  goToViewAllOutlets(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigateByUrl(SETTINGS_PAGE_PATHS.outlets);
  }
}
